import { EXTENSION_IDS } from './env';

/**
 * The website's side of the messages between the site and the installed extension
 * (Docs/SYNC_CONTRACT.md, section 8). Everything resolves; nothing throws: a browser without
 * `chrome.runtime` (Firefox, Safari, phones), an extension that is not installed, or one that
 * does not answer all simply mean "not installed".
 */

/** How long to wait for one extension id before treating it as absent. */
const ANSWER_TIMEOUT_MS = 2000;

type Reply = { ok?: boolean; version?: unknown } | undefined;

/** The part of `chrome.runtime` the bridge uses (the tests pass a fake). */
export interface ExtensionRuntime {
  sendMessage(extensionId: string, message: { type: string }): Promise<unknown>;
}

export type StartReviewResult = 'opened' | 'missing' | 'failed';

export interface ExtensionBridge {
  /** True when an extension with one of the configured ids answers a ping. */
  isExtensionInstalled(): Promise<boolean>;
  /** The installed extension's version ("4.1.0"), or null when none answers. */
  extensionVersion(): Promise<string | null>;
  /** Asks the extension to open the review window. `missing`: no extension answered. */
  startReview(): Promise<StartReviewResult>;
  /** Asks the extension to pull from the server now. False when it could not be asked. */
  syncNow(): Promise<boolean>;
}

function browserRuntime(): ExtensionRuntime | null {
  const runtime = typeof chrome !== 'undefined' ? chrome.runtime : undefined;
  return typeof runtime?.sendMessage === 'function'
    ? (runtime as unknown as ExtensionRuntime)
    : null;
}

export function createExtensionBridge(
  ids: string[],
  getRuntime: () => ExtensionRuntime | null = browserRuntime
): ExtensionBridge {
  /** The id that answered last, tried first next time. */
  let knownId: string | null = null;
  /** The version that extension reported. */
  let knownVersion: string | null = null;

  async function send(id: string, type: string): Promise<Reply> {
    const runtime = getRuntime();
    if (!runtime) return undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      const timeout = new Promise<undefined>((resolve) => {
        timer = setTimeout(() => resolve(undefined), ANSWER_TIMEOUT_MS);
      });
      return (await Promise.race([runtime.sendMessage(id, { type }), timeout])) as Reply;
    } catch {
      return undefined; // no such extension, or it does not accept this site
    } finally {
      clearTimeout(timer);
    }
  }

  /** The id of the installed extension, or null. */
  async function findExtension(): Promise<string | null> {
    if (!getRuntime()) return null;
    const candidates = knownId ? [knownId, ...ids.filter((id) => id !== knownId)] : ids;
    for (const id of candidates) {
      const reply = await send(id, 'lingoping.ping');
      if (reply?.ok === true) {
        knownId = id;
        knownVersion = typeof reply.version === 'string' ? reply.version : null;
        return id;
      }
    }
    knownId = null;
    knownVersion = null;
    return null;
  }

  return {
    async isExtensionInstalled() {
      return (await findExtension()) !== null;
    },

    async extensionVersion() {
      return (await findExtension()) ? knownVersion : null;
    },

    async startReview() {
      const id = await findExtension();
      if (!id) return 'missing';
      return (await send(id, 'lingoping.start_review'))?.ok === true ? 'opened' : 'failed';
    },

    async syncNow() {
      const id = await findExtension();
      if (!id) return false;
      return (await send(id, 'lingoping.sync_now'))?.ok === true;
    },
  };
}

export const { isExtensionInstalled, extensionVersion, startReview, syncNow } =
  createExtensionBridge(EXTENSION_IDS);

/** The first extension version that keeps one card per meaning of a word. */
export const SENSES_SINCE = '4.1.0';

/** Whether version `a` is `b` or newer ("4.10.0" is newer than "4.9.0"). */
export function versionAtLeast(a: string | null, b: string): boolean {
  if (!a) return false;
  const left = a.split('.').map(Number);
  const right = b.split('.').map(Number);
  for (let i = 0; i < Math.max(left.length, right.length); i++) {
    const l = left[i] ?? 0;
    const r = right[i] ?? 0;
    if (Number.isNaN(l) || Number.isNaN(r)) return false;
    if (l !== r) return l > r;
  }
  return true;
}
